import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { adminCreateUserSchema, type AdminCreateUserInput } from '@/lib/validation/schemas';
import { UserPlus, X } from 'lucide-react';

interface AddUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: AdminCreateUserInput) => void;
}

export function AddUserDrawer({ isOpen, onClose, onSuccess }: AddUserDrawerProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AdminCreateUserInput>({
    resolver: zodResolver(adminCreateUserSchema),
    defaultValues: {
      name: '',
      email: '',
      roleName: 'member',
    },
  });

  const handleClose = (nextOpen: boolean = false) => {
    if (!nextOpen && !isSubmitting) {
      reset();
      onClose();
    }
  };

  const onSubmit = async (data: AdminCreateUserInput) => {
    try {
      const response = await api.post('/api/admin/users', data);

      if (response.success) {
        toast.success('User created successfully', {
          description: `${data.name} has been added to the platform.`,
        });
        onSuccess(data);
        reset();
        onClose();
      } else {
        toast.error('Failed to create user', {
          description: response.error?.message || 'An unexpected error occurred.',
        });
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user', {
        description: 'Please check your connection and try again.',
      });
    } finally {
      // isSubmitting is handled by react-hook-form
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="max-w-md mx-auto">
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add New User
          </DrawerTitle>
          <DrawerDescription>
            Create a new user account with the specified role and permissions.
          </DrawerDescription>
        </DrawerHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="px-4 pb-4">
          <div className="space-y-4">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter full name"
                {...register('name')}
                disabled={isSubmitting}
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message as string}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                {...register('email')}
                disabled={isSubmitting}
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message as string}</p>
              )}
            </div>

            {/* Role Field */}
            <div className="space-y-2">
              <Label htmlFor="role">
                Role <span className="text-destructive">*</span>
              </Label>
              <Select
                value={watch('roleName')}
                onValueChange={(value) => setValue('roleName', value as AdminCreateUserInput['roleName'])}
                disabled={isSubmitting}
              >
                <SelectTrigger className={errors.roleName ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="pastor">Pastor</SelectItem>
                  <SelectItem value="media_team">Media Team</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              {errors.roleName && (
                <p className="text-sm text-destructive">{errors.roleName.message as string}</p>
              )}
            </div>
          </div>
        </form>

        <DrawerFooter>
          <Button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Creating User...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Create User
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose()}
            disabled={isSubmitting}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
